import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import {
  LIST_WHATSAPP_TEMPLATES,
  CREATE_WHATSAPP_TEMPLATE,
  SEND_CAMPAIGN,
  DELETE_WHATSAPP_TEMPLATE,
  CREATE_COUPON,
} from '~/services/campaigns';
import {
  PlusIcon,
  PaperAirplaneIcon,
  EyeIcon,
  TrashIcon,
  ArrowPathIcon,
  TicketIcon,
  WifiIcon,
} from '@heroicons/react/24/outline';
import { Discuss } from 'react-loader-spinner';
import { useLayoutContext } from '~/components/layout/Layout';

interface TemplatesModuleProps {
  agentId: string;
  channel: any; // Keep for backward compatibility, but we'll use multiple channels now
}

export const TemplatesModule = ({ agentId, channel }: TemplatesModuleProps) => {
  const [t] = useTranslation('campaigns');
  const navigate = useNavigate();
  const { renderToast } = useLayoutContext();

  // Enhanced state for multi-channel support
  const [selectedChannelId, setSelectedChannelId] = useState<string>('ALL');
  const [filter, setFilter] = useState<
    'ALL' | 'APPROVED' | 'PENDING' | 'REJECTED'
  >('ALL');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showSendDialog, setShowSendDialog] = useState<string | null>(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState<any | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState<any | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [variableValues, setVariableValues] = useState<string[]>([]);
  const [showCouponDialog, setShowCouponDialog] = useState(false);
  const [isGeneratingCoupon, setIsGeneratingCoupon] = useState(false);

  // Fetch templates using enhanced query
  const { data, loading, refetch } = useQuery(LIST_WHATSAPP_TEMPLATES, {
    variables: {
      data: {
        agent_id: agentId,
      },
    },
  });

  // Send campaign mutation
  const [sendCampaign] = useMutation(SEND_CAMPAIGN, {
    onCompleted: (data) => {
      setIsSending(false);
      setShowSendDialog(null);
      setPhoneNumber('');

      if (data.send_campaign.hasError) {
        renderToast({
          message: `Send failed: ${data.send_campaign.message}`,
          type: 'error',
        });
      } else {
        renderToast({
          message: 'Template sent successfully!',
          type: 'success',
        });
      }
    },
    onError: (error) => {
      setIsSending(false);
      renderToast({
        message: `Send failed: ${error.message}`,
        type: 'error',
      });
    },
  });

  // Delete template mutation
  const [deleteTemplate] = useMutation(DELETE_WHATSAPP_TEMPLATE, {
    onCompleted: (data) => {
      setIsDeleting(false);
      setShowDeleteDialog(null);
      const response = data.deleteWhatsappTemplate;

      if (response.hasError) {
        renderToast({
          message: response.message,
          type: 'error',
        });
      } else {
        renderToast({
          message: response.message,
          type: 'success',
        });
        refetch();
      }
    },
    onError: (error) => {
      setIsDeleting(false);
      renderToast({
        message: `Delete failed: ${error.message}`,
        type: 'error',
      });
    },
  });

  // Create coupon mutation
  const [createCoupon] = useMutation(CREATE_COUPON, {
    onCompleted: (data) => {
      setIsGeneratingCoupon(false);
      setShowCouponDialog(false);

      if (data.createCoupon.hasError) {
        renderToast({
          message: data.createCoupon.message,
          type: 'error',
        });
      } else {
        renderToast({
          message: `¡Cupón creado exitosamente! Código: ${data.createCoupon.data?.coupon_code}`,
          type: 'success',
        });
      }
    },
    onError: (error) => {
      setIsGeneratingCoupon(false);
      renderToast({
        message: `Error creating coupon: ${error.message}`,
        type: 'error',
      });
    },
  });

  // Parse enhanced data structure
  const responseData = data?.list_whatsapp_templates?.data;
  const allChannels = responseData?.channels || [];
  const allTemplates = responseData?.templates || [];
  const integrations = responseData?.integrations || {};
  const couponCapabilities = responseData?.couponCapabilities || {};

  // Debug logging (can be removed in production)
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 Channel and Template Debug Info:');
    console.log(
      'All Channels:',
      allChannels.map((ch: any) => ({
        _id: ch._id,
        whatsapp_number: ch.whatsapp_number,
      })),
    );
    console.log(
      'All Templates:',
      allTemplates.map((t: any) => ({
        _id: t._id,
        name: t.meta_template_name,
        channel_id: t.channel_id,
        status: t.status,
      })),
    );
    console.log('Selected Channel ID:', selectedChannelId);
  }

  // Backward compatibility - use first channel if no specific channel selected
  const channelData =
    selectedChannelId === 'ALL'
      ? allChannels[0]
      : allChannels.find((ch: any) => ch._id === selectedChannelId) ||
        allChannels[0];

  // Filter templates by selected channel (if not "ALL")
  const templates =
    selectedChannelId === 'ALL'
      ? allTemplates
      : allTemplates.filter(
          (template: any) => template.channel_id === selectedChannelId,
        );

  if (process.env.NODE_ENV === 'development') {
    console.log('Filtered Templates Count:', templates.length);
    console.log(
      'Templates for selected channel:',
      templates.map((t: any) => t.meta_template_name),
    );
  }

  const handleSend = async (templateId: string) => {
    if (!phoneNumber.trim()) {
      renderToast({
        message: 'Please enter a phone number',
        type: 'warning',
      });
      return;
    }

    // Get the template to check for required variables
    const template = templates.find((t: any) => t._id === templateId);
    const templateVariables =
      template?.template?.variables || template?.variables || [];

    // Validate that all required variables are filled
    if (templateVariables.length > 0) {
      const missingVariables = templateVariables.some(
        (_variable: any, index: number) => !variableValues[index]?.trim(),
      );

      if (missingVariables) {
        renderToast({
          message: 'Please fill in all template variables',
          type: 'warning',
        });
        return;
      }
    }

    setIsSending(true);

    try {
      await sendCampaign({
        variables: {
          data: {
            agent_id: agentId,
            channel_id: channelData._id,
            template_id: templateId,
            contacts: [
              {
                phoneNumber: phoneNumber,
                values: variableValues.slice(0, templateVariables.length), // Only send the needed values
              },
            ],
          },
        },
      });
    } catch (error) {
      console.error('Send failed:', error);
      setIsSending(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Refetch templates (sync happens automatically in the query)
      await refetch();
      setIsRefreshing(false);
    } catch (error) {
      console.error('Refresh failed:', error);
      setIsRefreshing(false);
    }
  };

  const handleDelete = async (template: any) => {
    setIsDeleting(true);
    try {
      await deleteTemplate({
        variables: {
          template_id: template._id,
          agent_id: agentId,
        },
      });
    } catch (error) {
      console.error('Delete failed:', error);
      setIsDeleting(false);
    }
  };

  const handleGenerateQuickCoupon = async (config: {
    discount_type: 'PERCENTAGE' | 'FIXED_AMOUNT';
    discount_value: number;
    minimum_purchase?: number;
    platform_id: 'native' | 'shopify';
    expires_in_days: number;
    usage_limit?: number;
    applies_once_per_customer?: boolean;
    coupon_code?: string;
  }) => {
    setIsGeneratingCoupon(true);
    try {
      await createCoupon({
        variables: {
          data: {
            agent_id: agentId,
            config,
          },
        },
      });
    } catch (error) {
      console.error('Coupon generation failed:', error);
      setIsGeneratingCoupon(false);
    }
  };

  const getBodyPreview = (template: any) => {
    const bodyComponent = template.template?.components?.find(
      (comp: any) => comp.type === 'BODY',
    );
    if (bodyComponent?.text) {
      return bodyComponent.text.length > 100
        ? bodyComponent.text.substring(0, 100) + '...'
        : bodyComponent.text;
    }
    return 'Sin contenido del cuerpo';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-custom-success-100 text-custom-success-300';
      case 'PENDING':
        return 'bg-custom-warning-100 text-custom-warning-200';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-custom-disabled-100 text-custom-disabled-200';
    }
  };

  const filteredTemplates = templates.filter((template: any) => {
    if (filter === 'ALL') return true;
    return template.status === filter;
  });

  const statusCounts = {
    ALL: templates.length,
    APPROVED: templates.filter((t: any) => t.status === 'APPROVED').length,
    PENDING: templates.filter((t: any) => t.status === 'PENDING').length,
    REJECTED: templates.filter((t: any) => t.status === 'REJECTED').length,
  };

  const isBarberiaTemplate = (template: any) =>
    template.meta_template_name?.includes('barbera') ||
    template.meta_template_name?.includes('barberia');

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Discuss visible height="60" width="60" ariaLabel="templates-loading" />
      </div>
    );
  }

  if (!channelData) {
    return (
      <div className="flex h-64 flex-col items-center justify-center text-center">
        <div className="mb-4 text-4xl">📱</div>
        <h3 className="text-lg font-medium text-gray-900">
          {t('no_channel_title')}
        </h3>
        <p className="mt-2 text-sm text-gray-600">
          {t('no_channel_description')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-medium text-gray-900">
            {t('templates_title')}
          </h2>

          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="btn btn-outline rounded-full"
            title="Refresh templates (auto-syncs with Meta API)"
          >
            <ArrowPathIcon
              className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
            />
            <span>{isRefreshing ? 'Actualizando...' : 'Actualizar'}</span>
          </button>
        </div>

        <div className="flex items-center space-x-3">
          {/* TODO: Integrate video compression into CreateTemplateModule when video > 16MB */}
          <button
            onClick={() => {
              // Pass the selected channel info via state
              const targetChannel =
                selectedChannelId === 'ALL'
                  ? allChannels[0]
                  : allChannels.find(
                      (ch: any) => ch._id === selectedChannelId,
                    ) || allChannels[0];

              navigate(`/agent/${agentId}/campaigns/templates/create`, {
                state: {
                  selectedChannel: targetChannel,
                  selectedChannelId: selectedChannelId,
                },
              });
            }}
            className="btn btn-primary rounded-full"
          >
            <PlusIcon className="mr-2 inline h-4 w-4" />
            {t('create_template')}
            {selectedChannelId !== 'ALL' && (
              <span className="ml-1 text-xs opacity-75">
                (
                {
                  allChannels.find((ch: any) => ch._id === selectedChannelId)
                    ?.whatsapp_number
                }
                )
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Channel Selector */}
      {allChannels.length > 1 && (
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <WifiIcon className="text-base-content/60 h-5 w-5" />
            <span className="text-base-content text-sm font-medium">
              {t('select_channel')}:
            </span>
          </div>
          <div className="max-w-md flex-1">
            <select
              className="select select-bordered w-full"
              value={selectedChannelId}
              onChange={(e) => setSelectedChannelId(e.target.value)}
            >
              <option value="ALL">
                {t('all_channels')} ({allTemplates.length} plantillas)
              </option>
              {allChannels.map((channel: any) => {
                const channelTemplates = allTemplates.filter(
                  (template: any) => template.channel_id === channel._id,
                );
                return (
                  <option key={channel._id} value={channel._id}>
                    {channel.whatsapp_number} ({channelTemplates.length}{' '}
                    plantillas)
                    {channel.config?.isConfigured ? ' ✓' : ' ⚠️'}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Integration Status Indicator */}
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              <div
                className={`h-2 w-2 rounded-full ${
                  integrations.hasShopifyIntegration
                    ? 'bg-blue-500'
                    : 'bg-green-500'
                }`}
              ></div>
              <span className="text-base-content/60 text-xs">
                {integrations.hasShopifyIntegration ? 'Shopify' : 'Nativo'}
              </span>
            </div>
            {integrations.hasShopifyIntegration &&
              integrations.shopifyConfig && (
                <span className="text-base-content/60 text-xs">
                  ({integrations.shopifyConfig.shop_name})
                </span>
              )}
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="tabs tabs-boxed bg-base-200">
        {(['ALL', 'APPROVED', 'PENDING', 'REJECTED'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`tab tab-lg gap-2 ${
              filter === status ? 'tab-active' : ''
            }`}
          >
            <span>{t(`status_${status.toLowerCase()}`)}</span>
            <span className="badge badge-sm">{statusCounts[status]}</span>
          </button>
        ))}
      </div>

      {/* Templates Grid */}
      {filteredTemplates.length > 0 ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredTemplates.map((template: any) => (
            <div
              key={template._id}
              className="border-custom-surface-200 bg-custom-surface-50 hover:border-custom-primary-accent-400 relative rounded-lg border p-6 shadow-sm transition-all hover:shadow-md"
            >
              {/* Template Header */}
              <div className="flex-1">
                <h3 className="truncate text-lg font-medium text-gray-900">
                  {template.template?.tbit_name || template.meta_template_name}
                </h3>
                <p className="mt-1 truncate text-sm text-gray-500">
                  {template.meta_template_name}
                </p>

                {/* Body Preview */}
                <div className="bg-base-100 mt-3 rounded-md p-3">
                  <p className="text-custom-surface-400 mb-1 text-xs font-medium uppercase tracking-wide">
                    {t('template_body_preview')}
                  </p>
                  <p className="text-sm leading-relaxed text-gray-700">
                    {getBodyPreview(template)}
                  </p>
                </div>
              </div>

              {/* Template Info Grid */}
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-custom-surface-400">
                    {t('template_language')}:
                  </span>
                  <span className="ml-1 font-medium text-gray-900">
                    {template.template?.language || 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="text-custom-surface-400">
                    {t('template_category')}:
                  </span>
                  <span className="ml-1 font-medium text-gray-900">
                    {template.category || 'N/A'}
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="text-custom-surface-400">
                    {t('template_created_at')}:
                  </span>
                  <span className="ml-1 font-medium text-gray-900">
                    {new Date(template.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              {/* Status Badge */}
              <div className="mt-4 flex items-center justify-between">
                <span
                  className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${getStatusColor(
                    template.status,
                  )}`}
                >
                  {template.status}
                </span>

                {/* Coupon Capability Badge */}
                {template.supportsCoupons && template.couponButtonConfig && (
                  <div className="flex items-center space-x-1">
                    <TicketIcon className="h-4 w-4 text-blue-500" />
                    <span className="text-xs font-medium text-blue-600">
                      Cupones
                    </span>
                  </div>
                )}
              </div>

              {/* Coupon Information (if enabled) */}
              {template.supportsCoupons && template.couponButtonConfig && (
                <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
                  <div className="mb-2 flex items-center space-x-2">
                    <TicketIcon className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-800">
                      Configuración de Cupón
                    </span>
                  </div>
                  <div className="space-y-1 text-xs text-blue-700">
                    <div>
                      <span className="font-medium">Plataforma:</span>{' '}
                      {template.couponButtonConfig.platform}
                    </div>
                    <div>
                      <span className="font-medium">ID Botón:</span>{' '}
                      <code className="rounded bg-blue-100 px-1 text-blue-800">
                        {template.couponButtonConfig.buttonId}
                      </code>
                    </div>
                    <div>
                      <span className="font-medium">Máx. texto:</span>{' '}
                      {template.couponButtonConfig.maxButtonTextLength}{' '}
                      caracteres
                    </div>
                    <div className="mt-2 border-t border-blue-200 pt-2">
                      <p className="font-medium text-blue-600">
                        💡 Cuando el usuario presione un botón en esta
                        plantilla, el bot generará automáticamente un cupón
                        basado en el texto del botón.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Special Notice for Barberia Template */}
              {isBarberiaTemplate(template) &&
                template.status === 'PENDING' && (
                  <div className="border-custom-warning-100 bg-custom-warning-100 mt-4 rounded-md border p-3">
                    <div className="text-sm">
                      <p className="text-custom-warning-200 font-medium">
                        🎬 Video size might exceed WhatsApp limit
                      </p>
                      <p className="text-custom-warning-200 mt-1 opacity-80">
                        Use video compression tool when available
                      </p>
                    </div>
                  </div>
                )}

              {/* Template Actions */}
              <div className="mt-6 space-y-2">
                {/* Primary Action - Send (only for approved) */}
                {template.status === 'APPROVED' && (
                  <button
                    onClick={() => setShowSendDialog(template._id)}
                    className="btn btn-primary w-full rounded-full"
                  >
                    <PaperAirplaneIcon className="h-4 w-4" />
                    <span>{t('send')}</span>
                  </button>
                )}

                {/* Secondary Actions */}
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowPreviewDialog(template)}
                    className="btn btn-outline flex-1 rounded-full"
                  >
                    <EyeIcon className="h-4 w-4" />
                    <span>{t('preview_template_button')}</span>
                  </button>

                  <button
                    onClick={() => setShowDeleteDialog(template)}
                    className="btn btn-error btn-outline flex-1 rounded-full"
                  >
                    <TrashIcon className="h-4 w-4" />
                    <span>{t('delete')}</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex h-64 flex-col items-center justify-center text-center">
          <div className="mb-4 text-4xl">📝</div>
          <h3 className="text-lg font-medium text-gray-900">
            {filter === 'ALL'
              ? 'No templates found'
              : `No ${filter.toLowerCase()} templates`}
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            {filter === 'ALL'
              ? 'Create your first template to start sending campaigns'
              : `No templates with ${filter.toLowerCase()} status found`}
          </p>
        </div>
      )}

      {/* Send Template Dialog - DaisyUI Style */}
      <dialog
        id="send_template_modal"
        className={`modal ${showSendDialog ? 'modal-open' : ''}`}
      >
        <div className="modal-box w-11/12 max-w-md">
          <form method="dialog">
            <button
              className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
              onClick={() => {
                setShowSendDialog(null);
                setVariableValues([]);
                setPhoneNumber('');
              }}
            >
              ✕
            </button>
          </form>

          {(() => {
            const template = templates.find(
              (t: any) => t._id === showSendDialog,
            );
            const templateVariables =
              template?.template?.variables || template?.variables || [];

            return (
              <>
                <h3 className="mb-4 text-lg font-bold">{t('send_template')}</h3>

                <div className="space-y-4">
                  {/* Template Info */}
                  <div className="bg-base-200 border-info rounded-lg border-l-4 p-3">
                    <p className="text-base-content text-sm font-medium">
                      <strong>{t('template')}:</strong>{' '}
                      {template?.template?.tbit_name ||
                        template?.meta_template_name}
                    </p>
                    <p className="text-base-content/60 text-xs">
                      {templateVariables.length > 0
                        ? `${templateVariables.length} ${t(
                            'variables_required',
                          )}`
                        : t('no_variables_required')}
                    </p>
                  </div>

                  {/* Phone Number */}
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">{t('phone_number')}</span>
                    </label>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="573244809494"
                      className="input input-bordered w-full"
                      disabled={isSending}
                    />
                  </div>

                  {/* Template Variables */}
                  {templateVariables.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-base-content text-sm font-bold">
                        {t('template_variables')}
                      </h4>
                      {templateVariables.map((variable: any, index: number) => (
                        <div key={index} className="form-control">
                          <label className="label">
                            <span className="label-text">
                              {variable.name} ({variable.component_type}) -{' '}
                              {t('position')} {variable.position}
                            </span>
                          </label>
                          <input
                            type="text"
                            value={variableValues[index] || ''}
                            onChange={(e) => {
                              const newValues = [...variableValues];
                              newValues[index] = e.target.value;
                              setVariableValues(newValues);
                            }}
                            placeholder={
                              variable.example ||
                              `${t('enter')} ${variable.name}`
                            }
                            className="input input-bordered w-full"
                            disabled={isSending}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="modal-action">
                  <button
                    onClick={() => {
                      setShowSendDialog(null);
                      setVariableValues([]);
                      setPhoneNumber('');
                    }}
                    disabled={isSending}
                    className="btn btn-ghost"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    onClick={() => showSendDialog && handleSend(showSendDialog)}
                    disabled={isSending || !phoneNumber.trim()}
                    className="btn btn-primary"
                  >
                    {isSending ? (
                      <>
                        <span className="loading loading-spinner loading-sm"></span>
                        {t('sending')}
                      </>
                    ) : (
                      t('send')
                    )}
                  </button>
                </div>
              </>
            );
          })()}
        </div>
        <form method="dialog" className="modal-backdrop">
          <button
            onClick={() => {
              setShowSendDialog(null);
              setVariableValues([]);
              setPhoneNumber('');
            }}
          >
            close
          </button>
        </form>
      </dialog>

      {/* Template Preview Dialog - DaisyUI Style */}
      <dialog
        id="template_preview_modal"
        className={`modal ${showPreviewDialog ? 'modal-open' : ''}`}
      >
        <div className="modal-box max-h-[80vh] w-11/12 max-w-2xl">
          <form method="dialog">
            <button
              className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
              onClick={() => setShowPreviewDialog(null)}
            >
              ✕
            </button>
          </form>

          <h3 className="mb-4 text-lg font-bold">
            {t('template_preview_title')}
          </h3>

          <div className="space-y-4">
            <div className="card bg-base-100 shadow">
              <div className="card-body">
                <h4 className="card-title">
                  {showPreviewDialog?.template?.tbit_name ||
                    showPreviewDialog?.meta_template_name}
                </h4>
                <div className="space-y-2 text-sm opacity-70">
                  <p>
                    <strong>Estado:</strong> {showPreviewDialog?.status}
                  </p>
                  <p>
                    <strong>Categoría:</strong> {showPreviewDialog?.category}
                  </p>
                  <p>
                    <strong>Idioma:</strong>{' '}
                    {showPreviewDialog?.template?.language}
                  </p>
                  <p>
                    <strong>Creado:</strong>{' '}
                    {showPreviewDialog?.created_at &&
                      new Date(
                        showPreviewDialog.created_at,
                      ).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Template Components Preview */}
            <div className="card bg-base-100 shadow">
              <div className="card-body">
                <h5 className="card-title text-base">
                  Componentes del Template:
                </h5>
                <div className="space-y-3">
                  {showPreviewDialog?.template?.components?.map(
                    (component: any, index: number) => (
                      <div key={index} className="bg-base-200 rounded p-3">
                        <div className="mb-2 flex items-center justify-between">
                          <span className="badge badge-outline">
                            {component.type}
                          </span>
                          {component.format && (
                            <span className="badge badge-ghost badge-sm">
                              {component.format}
                            </span>
                          )}
                        </div>
                        {component.text && (
                          <p className="text-sm">{component.text}</p>
                        )}
                        {component.buttons && (
                          <div className="mt-2 space-y-1">
                            {component.buttons.map(
                              (button: any, btnIndex: number) => (
                                <div
                                  key={btnIndex}
                                  className="badge badge-info"
                                >
                                  {button.type}: {button.text}
                                </div>
                              ),
                            )}
                          </div>
                        )}
                      </div>
                    ),
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="modal-action">
            <button
              onClick={() => setShowPreviewDialog(null)}
              className="btn btn-primary"
            >
              Cerrar
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setShowPreviewDialog(null)}>close</button>
        </form>
      </dialog>

      {/* Delete Confirmation Dialog - DaisyUI Style */}
      <dialog
        id="delete_template_modal"
        className={`modal ${showDeleteDialog ? 'modal-open' : ''}`}
      >
        <div className="modal-box">
          <form method="dialog">
            <button
              className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
              onClick={() => setShowDeleteDialog(null)}
            >
              ✕
            </button>
          </form>

          <div className="mb-4 flex items-center">
            <div className="flex-shrink-0">
              <TrashIcon className="text-error h-6 w-6" />
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-bold">
                {t('template_delete_confirm')}
              </h3>
            </div>
          </div>

          <div className="py-4">
            <p className="mb-4 text-sm opacity-70">
              {t('template_delete_warning')}
            </p>
            <div className="bg-base-200 border-error rounded-lg border-l-4 p-3">
              <p className="text-base-content font-medium">
                <strong>Plantilla:</strong>{' '}
                {showDeleteDialog?.template?.tbit_name ||
                  showDeleteDialog?.meta_template_name}
              </p>
            </div>
          </div>

          <div className="modal-action">
            <button
              onClick={() => setShowDeleteDialog(null)}
              disabled={isDeleting}
              className="btn btn-ghost"
            >
              {t('cancel')}
            </button>
            <button
              onClick={() => showDeleteDialog && handleDelete(showDeleteDialog)}
              disabled={isDeleting}
              className="btn btn-error"
            >
              {isDeleting ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Eliminando...
                </>
              ) : (
                t('delete')
              )}
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setShowDeleteDialog(null)}>close</button>
        </form>
      </dialog>

      {/* Quick Coupon Generation Dialog - DaisyUI Style */}
      <dialog
        id="quick_coupon_modal"
        className={`modal ${showCouponDialog ? 'modal-open' : ''}`}
      >
        <div className="modal-box">
          <form method="dialog">
            <button
              className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
              onClick={() => setShowCouponDialog(false)}
            >
              ✕
            </button>
          </form>

          <div className="mb-4 flex items-center space-x-2">
            <TicketIcon className="text-warning h-6 w-6" />
            <h3 className="text-lg font-bold">Generar Cupón Rápido</h3>
          </div>

          <QuickCouponForm
            onGenerate={handleGenerateQuickCoupon}
            onCancel={() => setShowCouponDialog(false)}
            isGenerating={isGeneratingCoupon}
          />
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setShowCouponDialog(false)}>close</button>
        </form>
      </dialog>
    </div>
  );
};

// Quick Coupon Form Component
const QuickCouponForm = ({
  onGenerate,
  onCancel,
  isGenerating,
}: {
  onGenerate: (config: any) => void;
  onCancel: () => void;
  isGenerating: boolean;
}) => {
  const [config, setConfig] = useState({
    discount_type: 'PERCENTAGE' as 'PERCENTAGE' | 'FIXED_AMOUNT',
    discount_value: 10,
    minimum_purchase: undefined as number | undefined,
    platform_id: 'native' as 'native' | 'shopify',
    expires_in_days: 30,
    usage_limit: undefined as number | undefined,
    applies_once_per_customer: true,
    coupon_code: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onGenerate(config);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {/* Discount Type */}
        <div className="form-control">
          <label className="label">
            <span className="label-text">Tipo</span>
          </label>
          <select
            value={config.discount_type}
            onChange={(e) =>
              setConfig({ ...config, discount_type: e.target.value as any })
            }
            className="select select-bordered w-full"
          >
            <option value="PERCENTAGE">Porcentaje (%)</option>
            <option value="FIXED_AMOUNT">Monto Fijo ($)</option>
          </select>
        </div>

        {/* Discount Value */}
        <div className="form-control">
          <label className="label">
            <span className="label-text">Valor</span>
          </label>
          <input
            type="number"
            value={config.discount_value}
            onChange={(e) =>
              setConfig({
                ...config,
                discount_value: parseInt(e.target.value) || 0,
              })
            }
            min="1"
            max={config.discount_type === 'PERCENTAGE' ? 100 : undefined}
            className="input input-bordered w-full"
          />
        </div>

        {/* Minimum Purchase */}
        <div className="form-control">
          <label className="label">
            <span className="label-text">Compra Mín.</span>
          </label>
          <input
            type="number"
            value={config.minimum_purchase || ''}
            onChange={(e) =>
              setConfig({
                ...config,
                minimum_purchase: e.target.value
                  ? parseInt(e.target.value)
                  : undefined,
              })
            }
            min="0"
            placeholder="Opcional"
            className="input input-bordered w-full"
          />
        </div>

        {/* Expires In Days */}
        <div className="form-control">
          <label className="label">
            <span className="label-text">Expira (días)</span>
          </label>
          <input
            type="number"
            value={config.expires_in_days}
            onChange={(e) =>
              setConfig({
                ...config,
                expires_in_days: parseInt(e.target.value) || 30,
              })
            }
            min="1"
            className="input input-bordered w-full"
          />
        </div>
      </div>

      {/* Platform */}
      <div className="form-control">
        <label className="label">
          <span className="label-text">Plataforma</span>
        </label>
        <select
          value={config.platform_id}
          onChange={(e) =>
            setConfig({ ...config, platform_id: e.target.value as any })
          }
          className="select select-bordered w-full"
        >
          <option value="native">Nativo (Interno)</option>
          <option value="shopify">Shopify</option>
        </select>
      </div>

      {/* Coupon Code */}
      <div className="form-control">
        <label className="label">
          <span className="label-text">Código (opcional)</span>
        </label>
        <input
          type="text"
          value={config.coupon_code}
          onChange={(e) =>
            setConfig({ ...config, coupon_code: e.target.value.toUpperCase() })
          }
          placeholder="Se genera automáticamente"
          className="input input-bordered w-full"
        />
      </div>

      {/* One Use Per Customer */}
      <div className="form-control">
        <label className="label cursor-pointer">
          <span className="label-text">Un uso por cliente</span>
          <input
            type="checkbox"
            checked={config.applies_once_per_customer}
            onChange={(e) =>
              setConfig({
                ...config,
                applies_once_per_customer: e.target.checked,
              })
            }
            className="checkbox checkbox-warning"
          />
        </label>
      </div>

      <div className="modal-action">
        <button
          type="button"
          onClick={onCancel}
          disabled={isGenerating}
          className="btn btn-ghost"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={
            isGenerating || !config.discount_value || !config.expires_in_days
          }
          className="btn btn-warning"
        >
          {isGenerating ? (
            <>
              <span className="loading loading-spinner loading-sm"></span>
              Generando...
            </>
          ) : (
            'Generar Cupón'
          )}
        </button>
      </div>
    </form>
  );
};
